/**
 * Business Calendar Service
 * Handles business day calculations, respecting weekends and holidays.
 * Loads holiday data dynamically from the backend API (Firestore business centers).
 */

import { getBusinessCenterHolidays } from './marketConventionsService';

class BusinessCalendarService {
  private holidays: Set<string> = new Set();
  private weekendDaysJS: Set<number> = new Set([0, 6]); // JS convention: Sun=0, Sat=6
  private loadedCentersKey: string = '';

  /**
   * Load holiday calendar from the backend for given business centers.
   * Deduplicates and skips if already loaded with the same set of centers.
   */
  async loadFromAPI(centers: string[]): Promise<void> {
    const unique = [...new Set(centers.filter(Boolean))];
    if (unique.length === 0) unique.push('SCL'); // Default
    const key = unique.sort().join(',');

    if (key === this.loadedCentersKey) return; // Already loaded

    try {
      const data = await getBusinessCenterHolidays(unique);

      // Convert Python weekendDays (Mon=0…Sun=6) to JS (Sun=0…Sat=6)
      this.weekendDaysJS = new Set(
        data.weekendDays.map(pyDay => (pyDay + 1) % 7)
      );

      // Flatten all holiday dates into a single set
      this.holidays = new Set<string>();
      for (const dates of Object.values(data.holidays)) {
        for (const d of dates) {
          this.holidays.add(d);
        }
      }

      this.loadedCentersKey = key;
    } catch (error) {
      console.error('Failed to load business calendar from API, using weekend-only fallback:', error);
      // Keep whatever was loaded before, or default weekends
      if (!this.loadedCentersKey) {
        this.weekendDaysJS = new Set([0, 6]);
        this.holidays = new Set();
      }
    }
  }

  /**
   * Check if a date is a weekend
   */
  isWeekend(date: Date): boolean {
    return this.weekendDaysJS.has(date.getDay());
  }

  /**
   * Check if a date is a holiday
   */
  isHoliday(date: Date): boolean {
    return this.holidays.has(this.formatDate(date));
  }

  /**
   * Check if a date is a business day (not weekend, not holiday)
   */
  isBusinessDay(date: Date): boolean {
    return !this.isWeekend(date) && !this.isHoliday(date);
  }

  /**
   * Add business days to a date.
   * Automatically skips weekends and holidays.
   */
  addBusinessDays(startDate: Date, businessDays: number): Date {
    let current = new Date(startDate);
    let daysAdded = 0;
    const direction = businessDays >= 0 ? 1 : -1;
    const targetDays = Math.abs(businessDays);

    while (daysAdded < targetDays) {
      current.setDate(current.getDate() + direction);
      if (this.isBusinessDay(current)) {
        daysAdded++;
      }
    }

    return current;
  }

  /**
   * Get the next business day from a given date.
   * If the date is already a business day, returns the same date.
   */
  getNextBusinessDay(date: Date): Date {
    let current = new Date(date);
    while (!this.isBusinessDay(current)) {
      current.setDate(current.getDate() + 1);
    }
    return current;
  }

  /**
   * Get the previous business day from a given date.
   * If the date is already a business day, returns the same date.
   */
  getPreviousBusinessDay(date: Date): Date {
    let current = new Date(date);
    while (!this.isBusinessDay(current)) {
      current.setDate(current.getDate() - 1);
    }
    return current;
  }

  /**
   * Count business days between two dates (inclusive)
   */
  countBusinessDays(startDate: Date, endDate: Date): number {
    let count = 0;
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      if (this.isBusinessDay(current)) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  }

  /**
   * Get holiday info for a specific date.
   * Returns null — holiday names are not available from the Firestore calendar.
   * Kept for API compatibility with BusinessDayDatePicker tooltips.
   */
  getHoliday(date: Date): { name: string } | null {
    if (this.holidays.has(this.formatDate(date))) {
      return { name: '' };
    }
    return null;
  }

  /**
   * Format date to ISO string (YYYY-MM-DD)
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

// Singleton instance
export const businessCalendar = new BusinessCalendarService();
